/**
 * Generic API response matching your backend format
 */
export interface ApiResponse<T> {
  status: "success" | "failure";
  code: string;
  data: T | null;
  error: string | Record<string, string> | null;
  statusCode: number;
}

export interface ApiError extends Error {
  data?: {
    error?: string | Record<string, string>;
    data?: null;
    code?: string;
  };
  code?: string;
  statusCode: number;
}

/**
 * Success response helper
 */
export interface JSuccessResponse<T> extends ApiResponse<T> {
  status: "success";
  data: T;
  error: null;
  statusCode: number;
}

/**
 * Error response helper
 */
export interface JErrorResponse extends ApiResponse<null> {
  status: "failure";
  data: null;
  error: string | Record<string, string>;
  statusCode: number;
}

// types/ofetch-error.ts
export interface OfetchError<T = unknown> {
  name: string;
  message: string;
  stack?: string;
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    _data: T;
  };
  request?: unknown;
}

// Helper to safely cast error
export function asOfetchError(error: unknown): OfetchError | null {
  if (error && typeof error === "object") {
    return error as OfetchError;
  }
  return null;
}

export function isJErrorResponse(value: unknown): value is JErrorResponse {
  if (typeof value !== "object" || value === null) return false;

  const v = value as Record<string, unknown>;

  return (
    (v.status === "failure" &&
      v.data === null &&
      typeof v.error === "string") ||
    typeof v.error === "object"
  );
}

/**
 * Common response codes (matching your backend)
 */
export const ResponseCode = {
  SUCCESS: "SUCCESS",
  VALIDATION_FAILED: "VALIDATION_FAILED",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  // Auth specific
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  EMAIL_ALREADY_EXISTS: "EMAIL_ALREADY_EXISTS",
  INVALID_TOKEN: "INVALID_TOKEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  REGISTRATION_ALREADY_COMPLETED: "REGISTRATION_ALREADY_COMPLETED",
  INVALID_OR_EXPIRED_SIGNUP_VERIFICATION_TOKEN:
    "INVALID_OR_EXPIRED_SIGNUP_VERIFICATION_TOKEN",
  INVALID_OR_EXPIRED_OTP_CHALLENGE_STATE:
    "INVALID_OR_EXPIRED_OTP_CHALLENGE_STATE",
  TOO_MANY_VERIFICATION_ATTEMPTS: "TOO_MANY_VERIFICATION_ATTEMPTS",
} as const;

export type ResponseCodeType = (typeof ResponseCode)[keyof typeof ResponseCode];

export type ApiClientWithServiceNamesape = {
  auth: {
    get: <T>(
      endpoint: string,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    post: <T>(
      endpoint: string,
      body?: RequestBody,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    put: <T>(
      endpoint: string,
      body?: RequestBody,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    patch: <T>(
      endpoint: string,
      body?: RequestBody,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    delete: <T>(
      endpoint: string,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
  };
  translator: {
    get: <T>(
      endpoint: string,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    post: <T>(
      endpoint: string,
      body?: RequestBody,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    put: <T>(
      endpoint: string,
      body?: RequestBody,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    patch: <T>(
      endpoint: string,
      body?: RequestBody,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
    delete: <T>(
      endpoint: string,
      options?: TypedFetchOptions,
    ) => Promise<ApiResponse<T>>;
  };
};
